import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Twitter, Linkedin, Instagram, Youtube, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';
import SparkLogo from './SparkLogo';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  // Use the current origin so the newsletter form works on preview, production
  // and any custom domain (e.g. usegetspark.com) without env-var coupling.
  const BACKEND_URL = window.location.origin;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMsg('Insira um e-mail válido.');
      return;
    }
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 4000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'Algo deu errado. Tente novamente.');
      setTimeout(() => { setStatus('idle'); setErrorMsg(''); }, 4000);
    }
  };

  const linkColumns = [
    {
      title: 'A plataforma',
      links: [
        { label: 'Como funciona', href: '#how' },
        { label: 'Quero Investir', href: '#investors' },
        { label: 'Tenho uma ideia', href: '#entrepreneurs' },
        { label: 'Por que aqui', href: '#differentiators' },
        { label: 'Histórias reais', href: '#testimonials' },
      ],
    },
    {
      title: 'Empresa',
      links: [
        { label: 'Sobre nós', href: '#top' },
        { label: 'Trabalhe conosco', href: '#top' },
        { label: 'Blog', href: '#top' },
        { label: 'Imprensa', href: '#top' },
        { label: 'Fale com a gente', href: 'mailto:contato@getspark.app' },
      ],
    },
    {
      title: 'Ajuda',
      links: [
        { label: 'Central de ajuda', href: '#top' },
        { label: 'Perguntas frequentes', href: '#top' },
        { label: 'Comunidade', href: '#top' },
        { label: 'Suporte', href: 'mailto:suporte@getspark.app' },
        { label: 'Parcerias', href: 'mailto:parcerias@getspark.app' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Termos de uso', href: '#top' },
        { label: 'Privacidade', href: '#top' },
        { label: 'Cookies', href: '#top' },
        { label: 'Segurança', href: '#top' },
      ],
    },
  ];

  return (
    <footer className="relative pt-20 pb-10 bg-gradient-to-b from-transparent via-[#03050d] to-[#020308] border-t border-white/5 z-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <a href="#top" className="flex items-center gap-3 mb-5">
              <SparkLogo height={52} />
            </a>

            <p className="text-sm text-slate-400 leading-relaxed max-w-md">
              A plataforma que conecta investidores a pequenos negócios. Onde ideias encontram oportunidade e nascem novas Sparks todos os dias.
            </p>

            {/* Newsletter */}
            <div className="mt-7">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Receba novidades</div>
              <form onSubmit={onSubmit} className="relative max-w-md">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                  <Mail size={16} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-32 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50 focus:bg-white/[0.07] transition-all"
                  disabled={status === 'loading' || status === 'success'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading' || status === 'success'}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 btn-primary rounded-lg px-4 py-2.5 text-xs inline-flex items-center gap-1.5"
                >
                  {status === 'success' ? (
                    <>
                      <Check size={13} /> Inscrito
                    </>
                  ) : status === 'loading' ? (
                    <>Enviando…</>
                  ) : (
                    <>
                      Assinar <ArrowRight size={13} />
                    </>
                  )}
                </button>
              </form>
              {status === 'error' && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-xs text-red-400">{errorMsg}</motion.p>
              )}
              {status === 'success' && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-xs text-green-400">Pronto! Você receberá nossas novidades.</motion.p>
              )}
            </div>

            {/* Social */}
            <div className="mt-7 flex items-center gap-3">
              {[
                { Icon: Twitter, label: 'Twitter', href: 'https://twitter.com/' },
                { Icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/' },
                { Icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/' },
                { Icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/' },
              ].map(({ Icon, label, href }, i) => (
                <a
                  key={i}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.04] border border-white/10 text-slate-400 hover:text-white hover:bg-white/[0.08] hover:border-[#4facfe]/40 transition-all"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {linkColumns.map((col, i) => (
              <div key={i}>
                <div className="text-xs uppercase tracking-widest text-white font-semibold">{col.title}</div>
                <ul className="mt-4 space-y-2.5">
                  {col.links.map((l, j) => (
                    <li key={j}>
                      <a
                        href={l.href}
                        className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1 group"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-slate-500">
            © {new Date().getFullYear()} GetSpark. Todos os direitos reservados.
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            Feito com <span className="text-[#ff7043]">♦</span> no Brasil
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
